/**
 * Rest Section
 * Handles collapsible FAQ items with smooth animations
 * Features: Smooth expand/collapse, accessibility, keyboard navigation
 */

class Rest {
  constructor() {
    this.sections = document.querySelectorAll('.rest');
    
    if (this.sections.length > 0) {
      this.init();
    }
  }

  init() {
    this.sections.forEach(section => {
      this.initFAQ(section);
    });
  }

  /**
   * Initialize FAQ functionality
   */
  initFAQ(section) {
    const questions = section.querySelectorAll('.rest__question');
    
    if (questions.length === 0) return;
    
    questions.forEach(question => {
      // Click handler
      question.addEventListener('click', () => {
        this.toggleFAQ(question);
      });
      
      // Keyboard handler
      question.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleFAQ(question);
        }
      });
    });
  }

  /**
   * Toggle FAQ item open/closed
   */
  toggleFAQ(questionButton) {
    const isExpanded = questionButton.getAttribute('aria-expanded') === 'true';
    const answerId = questionButton.getAttribute('aria-controls');
    const answer = document.getElementById(answerId);
    
    if (!answer) return;
    
    if (isExpanded) {
      this.closeFAQ(questionButton, answer);
    } else {
      this.openFAQ(questionButton, answer);
    }
  }

  /**
   * Open FAQ item
   */
  openFAQ(questionButton, answer) {
    // Update button state
    questionButton.setAttribute('aria-expanded', 'true');
    answer.setAttribute('aria-hidden', 'false');
    
    // Add active class for styling
    questionButton.classList.add('rest__question--active');
    
    // Smooth animation
    this.animateOpen(answer);
    
    // Track analytics
    this.trackFAQOpen(questionButton);
  }

  /**
   * Close FAQ item
   */
  closeFAQ(questionButton, answer) {
    // Update button state
    questionButton.setAttribute('aria-expanded', 'false');
    answer.setAttribute('aria-hidden', 'true');
    
    // Remove active class
    questionButton.classList.remove('rest__question--active');
    
    // Smooth animation
    this.animateClose(answer);
  }

  /**
   * Animate opening FAQ
   */
  animateOpen(answer) {
    // Get the natural height
    answer.style.maxHeight = 'none';
    const naturalHeight = answer.offsetHeight;
    answer.style.maxHeight = '0px';
    
    // Force reflow
    answer.offsetHeight;
    
    // Animate to natural height
    answer.style.maxHeight = naturalHeight + 'px';
    
    // Clean up after animation
    setTimeout(() => {
      if (answer.getAttribute('aria-hidden') === 'false') {
        answer.style.maxHeight = 'none';
      }
    }, 400);
  }

  /**
   * Animate closing FAQ
   */
  animateClose(answer) {
    // Get current height
    const currentHeight = answer.offsetHeight;
    answer.style.maxHeight = currentHeight + 'px';
    
    // Force reflow
    answer.offsetHeight;
    
    // Animate to 0
    answer.style.maxHeight = '0px';
    
    // Clean up after animation
    setTimeout(() => {
      if (answer.getAttribute('aria-hidden') === 'true') {
        answer.style.maxHeight = '0px';
      }
    }, 400);
  }

  /**
   * Track FAQ open for analytics
   */
  trackFAQOpen(questionButton) {
    const questionText = questionButton.querySelector('.rest__question-text')?.textContent;
    
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', 'faq_open', {
        event_category: 'engagement',
        event_label: questionText || 'FAQ Question',
        value: 1
      });
    }
    
    // Custom event for other tracking
    window.dispatchEvent(new CustomEvent('restFAQOpen', {
      detail: { 
        question: questionText,
        section: questionButton.closest('.rest').dataset.sectionId
      }
    }));
  }

  /**
   * Open all FAQ items (utility method)
   */
  openAll(section) {
    const questions = section.querySelectorAll('.rest__question');
    questions.forEach(question => {
      const answerId = question.getAttribute('aria-controls');
      const answer = document.getElementById(answerId);
      if (answer && question.getAttribute('aria-expanded') === 'false') {
        this.openFAQ(question, answer);
      }
    });
  }

  /**
   * Close all FAQ items (utility method)
   */
  closeAll(section) {
    const questions = section.querySelectorAll('.rest__question');
    questions.forEach(question => {
      const answerId = question.getAttribute('aria-controls');
      const answer = document.getElementById(answerId);
      if (answer && question.getAttribute('aria-expanded') === 'true') {
        this.closeFAQ(question, answer);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new Rest();
  });
} else {
  new Rest();
}

// Reinitialize on Shopify section events (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new Rest();
  });
  
  document.addEventListener('shopify:section:unload', () => {
    // Clean up if needed
  });
}

// Export for external use
window.Rest = Rest;
